import { Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import { FormGroup, AbstractControl } from '@angular/forms';
import { AmountService } from '@nano/common/amount';
import { Classifier } from '@nano/common/classifier';
import { CustomInputNumberParser } from '@nano/common/util';
import { PaymentConditions, PaymentConfig, ZeroDecimalCurrency } from '@nano/payment/payment';
import { UntilDestroy } from '@ngneat/until-destroy';
import { L10N_LOCALE, L10nLocale } from 'angular-l10n';

@UntilDestroy()
@Component({
  selector: 'app-payment-amount',
  templateUrl: './payment-amount.component.html',
  styleUrls: ['./payment-amount.component.less'],
  standalone: false
})
export class PaymentAmountComponent {
  readonly CustomInputNumberParser = CustomInputNumberParser;
  @Input() amountControl: AbstractControl;
  @Input() currencyControl: AbstractControl;
  @Input() exchangeAmountControl: AbstractControl;
  @Input() exchangeCurrencyControl: AbstractControl;
  @Input() form: FormGroup;
  @Input() paymentConfig: PaymentConfig;
  @Input() allowCurrencySelection: boolean;
  @Input() showReadOnlyCurrency: boolean;
  @Input() paymentConditions: PaymentConditions;
  @Input() currencies: Classifier[];
  @Input() shouldShowExchangeAmount: boolean;
  @Output() amountFocusChange = new EventEmitter<boolean>();
  @Output() exchangeAmountFocusChange = new EventEmitter<boolean>();

  constructor(
    @Inject(L10N_LOCALE) public locale: L10nLocale,
    private amountService: AmountService
  ) {}

  private getFeeDisplayType(feeCurrency: string, compareCurrency: string): string {
    if (!this.paymentConditions?.total || !this.paymentConditions?.fee) {
      return 'none';
    }
    if (this.paymentConditions.tieredFeesApplied) {
      return feeCurrency === compareCurrency ? 'tieredSameCurrency' : 'tieredDifferentCurrency';
    }
    return feeCurrency === compareCurrency ? 'nonTieredSameCurrency' : 'nonTieredDifferentCurrency';
  }

  get feeDisplayType(): string {
    return this.getFeeDisplayType(this.paymentConditions?.fee?.currency, this.paymentConditions?.currency);
  }

  get exchangeFeeDisplayType(): string {
    return this.getFeeDisplayType(this.paymentConditions?.fee?.currency, this.paymentConditions?.initialCurrency);
  }

  handleKeyDown(event: KeyboardEvent): void {
    this.amountService.handleKeyDown(event);
  }

  onAmountFocusChange(focused: boolean): void {
    this.amountFocusChange.emit(focused);
  }

  onExchangeAmountFocusChange(focused: boolean): void {
    this.exchangeAmountFocusChange.emit(focused);
  }

  get amountPrecision(): number {
    const currency = this.currencyControl?.value as ZeroDecimalCurrency;
    return Object.values(ZeroDecimalCurrency).includes(currency) ? 0 : this.paymentConfig.amountPrecision;
  }

  get exchangeAmountPrecision(): number {
    const currency = this.exchangeCurrencyControl?.value as ZeroDecimalCurrency;
    return Object.values(ZeroDecimalCurrency).includes(currency) ? 0 : this.paymentConfig.amountPrecision;
  }
}
